import React, { useState, useEffect } from 'react';
import {
  Users, Heart, CreditCard, Star,
  ArrowRight, Activity, ShieldCheck, Eye, RefreshCw,
  Download, Zap, CheckCircle2, FileJson, FileSpreadsheet, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  fetchAdminStats,
  fetchExportSyncData,
  fetchAdminUsers,
  fetchAdminPayments,
  downloadJSON,
  downloadCSV,
  convertUsersToCSV
} from '../utils/api';

const Sparkline: React.FC<{ data: number[]; color: string }> = ({ data, color }) => {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const points = data
    .map((d, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 30 - ((d - min) / (max - min || 1)) * 25;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg className="w-24 h-8 overflow-visible" viewBox="0 0 100 30">
      <polyline fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
};

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [recentPayments, setRecentPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    loadData(false);
    const handleModeChanged = () => {
      loadData(false);
    };
    window.addEventListener('system-mode-changed', handleModeChanged);
    return () => window.removeEventListener('system-mode-changed', handleModeChanged);
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const loadData = async (notify = true) => {
    setLoading(true);
    try {
      const [statsData, usersData, paymentsData] = await Promise.all([
        fetchAdminStats(),
        fetchAdminUsers(),
        fetchAdminPayments(),
      ]);
      setStats(statsData);

      if (usersData?.users && usersData.users.length > 0) {
        setRecentUsers(usersData.users.slice(0, 5).map((u: any) => ({
          name: u.name,
          phone: u.phone,
          plan: u.plan_type === 'chat_week' ? 'Elite Weekly' : u.plan_type === 'chat_2hr' ? '2-Hour Chat' : u.plan_type === 'match' ? 'Instant Match' : 'Free',
          status: u.status,
          joined: u.created_at ? `${u.created_at.slice(0, 10)}` : 'Recently',
          avatar: (u.name || 'AM').split(' ').map((p: string) => p[0]).join('').slice(0, 2).toUpperCase(),
        })));
      } else {
        setRecentUsers([]);
      }

      if (paymentsData?.transactions && paymentsData.transactions.length > 0) {
        setRecentPayments(paymentsData.transactions.slice(0, 5).map((t: any) => ({
          user: t.user,
          amount: t.amount,
          plan: t.plan,
          method: t.method,
          status: t.status,
          time: t.date ? `${t.date.slice(11, 16) || t.date}` : 'Recently',
        })));
      } else {
        setRecentPayments([]);
      }
    } catch (e) {
      console.warn('Dashboard loadData error:', e);
    } finally {
      setLoading(false);
    }

    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLastUpdated(timeStr);
    if (notify) {
      showToast(`✅ Platform metrics refreshed at ${timeStr} (Cloudflare D1 synced)`);
    }
  };

  const handleExportSync = async () => {
    setIsSyncing(true);
    try {
      const data = await fetchExportSyncData();
      const filename = `amoura-system-sync-${new Date().toISOString().slice(0, 10)}.json`;
      downloadJSON(filename, data);
      showToast('⚡ Export Sync completed! System telemetry snapshot downloaded.');
    } catch (e) {
      showToast('❌ Failed to complete Export Sync');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleExportUsersCSV = async () => {
    const res = await fetchAdminUsers();
    const csv = convertUsersToCSV(res.users || []);
    downloadCSV(`amoura-users-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    showToast(`✅ Exported ${res.users?.length || 0} user records to CSV`);
    setShowExportModal(false);
  };

  const statCards = [
    {
      label: 'Total Verified Users',
      value: stats && stats.totalUsers !== undefined ? stats.totalUsers.toLocaleString() : '0',
      change: 'Real D1',
      up: true,
      icon: Users,
      color: '#3b82f6',
      bg: 'rgba(59,130,246,0.1)',
      trend: [0, 1, 2, 3, 5, 6, stats?.totalUsers || 0],
    },
    {
      label: 'Active Subscriptions',
      value: stats && stats.activeSubscriptions !== undefined ? stats.activeSubscriptions.toLocaleString() : '0',
      change: 'Squad Billing',
      up: true,
      icon: Star,
      color: '#8b5cf6',
      bg: 'rgba(139,92,246,0.1)',
      trend: [0, 1, 1, 2, 3, 4, stats?.activeSubscriptions || 0],
    },
    {
      label: 'Revenue (Squad NGN)',
      value: `₦${(stats && stats.revenueNGN !== undefined ? stats.revenueNGN : 0).toLocaleString()}`,
      change: 'Live Gross',
      up: true,
      icon: CreditCard,
      color: '#10b981',
      bg: 'rgba(16,185,129,0.1)',
      trend: [0, 500, 1000, 2000, 5000, stats?.revenueNGN || 0],
    },
    {
      label: 'Daily Matches (Edge DO)',
      value: stats && stats.dailyMatches !== undefined ? stats.dailyMatches.toLocaleString() : '0',
      change: 'Edge Rooms',
      up: true,
      icon: Heart,
      color: '#FD297B',
      bg: 'rgba(253,41,123,0.1)',
      trend: [0, 1, 1, 2, 4, stats?.dailyMatches || 0],
    },
  ];

  return (
    <div className="space-y-6">
      {/* ── Toast Notification ── */}
      {toastMessage && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#141428] border border-emerald-500/40 text-white shadow-2xl shadow-emerald-500/10">
          <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
          <p className="text-xs font-bold">{toastMessage}</p>
        </div>
      )}

      {/* ── Export Sync Hub Modal ── */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0d0d1a] border border-[#1e1e3a] rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap size={18} className="text-amber-400" />
                <h3 className="text-sm font-bold text-white font-['Outfit']">Export & Edge Sync Hub</h3>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className="p-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Export synchronized database telemetry from Cloudflare D1 and transaction ledgers from SquadCo.
            </p>

            <div className="space-y-2.5 pt-2">
              <button
                onClick={handleExportSync}
                className="w-full flex items-center justify-between p-3.5 rounded-xl bg-[#141428] border border-[#1e1e3a] hover:border-pink-500/40 transition-all text-left cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-pink-500/20 text-pink-400 flex items-center justify-center">
                    <FileJson size={16} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white group-hover:text-pink-400 transition-colors">
                      Full System Telemetry (JSON)
                    </p>
                    <p className="text-[11px] text-zinc-500">Live D1 counters, KV prekeys & cache status</p>
                  </div>
                </div>
                <Download size={14} className="text-zinc-500 group-hover:text-white" />
              </button>

              <button
                onClick={handleExportUsersCSV}
                className="w-full flex items-center justify-between p-3.5 rounded-xl bg-[#141428] border border-[#1e1e3a] hover:border-emerald-500/40 transition-all text-left cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                    <FileSpreadsheet size={16} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white group-hover:text-emerald-400 transition-colors">
                      Users Directory (CSV)
                    </p>
                    <p className="text-[11px] text-zinc-500">All registered users, Trust Scores & KYC levels</p>
                  </div>
                </div>
                <Download size={14} className="text-zinc-500 group-hover:text-white" />
              </button>
            </div>

            <div className="pt-2">
              <button
                onClick={() => setShowExportModal(false)}
                className="w-full py-2 text-xs font-bold rounded-xl bg-zinc-800 text-zinc-300 hover:text-white cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-['Outfit']">Dashboard Overview</h1>
            {stats?.mode === 'test' ? (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                🧪 Test Sandbox
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                ⚡ Live D1 Engine
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            {stats?.mode === 'test'
              ? 'Displaying simulated QA metrics & sandbox projections.'
              : 'Real-time platform metrics aggregated from Cloudflare D1 & Squad Payments.'}
          </p>
        </div>

        {/* Action CTAs: Refresh Metrics & Export Sync */}
        <div className="flex items-center gap-2.5 flex-wrap self-start sm:self-auto">
          {lastUpdated && (
            <span className="text-[11px] text-zinc-500 hidden md:inline">
              Updated {lastUpdated}
            </span>
          )}

          <button
            onClick={() => loadData(true)}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-zinc-300 hover:text-white hover:border-zinc-500 transition-colors cursor-pointer"
            title="Refresh metrics from Cloudflare D1"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh Metrics</span>
          </button>

          <button
            onClick={handleExportSync}
            disabled={isSyncing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white shadow-lg shadow-pink-500/25 transition-all cursor-pointer disabled:opacity-50"
            title="Synchronize edge cache & download telemetry snapshot"
          >
            {isSyncing ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
            <span>{isSyncing ? 'Syncing...' : 'Export Sync'}</span>
          </button>

          <button
            onClick={() => setShowExportModal(true)}
            className="p-2 rounded-xl bg-[#141428] border border-[#1e1e3a] text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors cursor-pointer"
            title="Export Options"
          >
            <Download size={14} />
          </button>
        </div>
      </div>

      {/* Action Banners for KYC & Moderation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/kyc"
          className="flex items-center justify-between p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 hover:border-amber-400 transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400">
              <ShieldCheck size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-white">Pending KYC Submissions</p>
              <p className="text-xs text-amber-400 font-medium">{stats?.pendingKyc ?? 0} submission(s) await review</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-amber-400 group-hover:translate-x-1 transition-transform" />
        </Link>

        <Link
          to="/moderation"
          className="flex items-center justify-between p-4 rounded-2xl bg-pink-500/10 border border-pink-500/30 hover:border-pink-400 transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center text-pink-400">
              <Eye size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-white">Flagged Content Moderation</p>
              <p className="text-xs text-pink-400 font-medium">{stats?.pendingMedia ?? 0} media upload(s) pending review</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-pink-400 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, i) => (
          <div
            key={i}
            className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] hover:border-zinc-600 transition-all shadow-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: card.bg }}>
                <card.icon size={20} style={{ color: card.color }} />
              </div>
              <Sparkline data={card.trend} color={card.color} />
            </div>

            <p className="text-2xl font-extrabold text-white font-['Outfit'] mb-1 tracking-tight">
              {card.value}
            </p>
            <div className="flex items-center justify-between">
              <p className="text-xs text-zinc-400 font-medium">{card.label}</p>
              <span className="text-xs font-bold text-emerald-400">{card.change}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Middle Row: Recent Signups & Live Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Signups Table */}
        <div className="lg:col-span-2 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e1e3a]">
            <div className="flex items-center gap-2">
              <Activity size={18} className="text-zinc-400" />
              <h2 className="text-base font-bold text-white">Recent Verified Signups</h2>
            </div>
            <Link to="/users" className="text-xs font-semibold text-pink-400 hover:text-pink-300 flex items-center gap-1">
              View All <ArrowRight size={13} />
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#141428] text-xs font-bold uppercase tracking-wider text-zinc-400 border-b border-[#1e1e3a]">
                <tr>
                  <th className="px-6 py-3">User</th>
                  <th className="px-6 py-3">Active Plan</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e1e3a]">
                {recentUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-zinc-500 text-xs">
                      No registered users in database yet. Click "+ Add User" in Users directory or sign up via Mobile app.
                    </td>
                  </tr>
                ) : (
                  recentUsers.map((u, i) => (
                    <tr key={i} className="hover:bg-[#141428]/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-pink-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white shrink-0">
                            {u.avatar}
                          </div>
                          <div>
                            <p className="font-semibold text-white">{u.name}</p>
                            <p className="text-xs text-zinc-400">{u.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-purple-500/10 text-purple-400 border border-purple-500/20">
                          {u.plan}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-lg ${
                          u.status === 'active'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-red-500/10 text-red-400 border border-red-500/20'
                        }`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-zinc-400">{u.joined}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Live Squad Payments Feed */}
        <div className="rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-4 border-b border-[#1e1e3a] mb-4">
              <h2 className="text-base font-bold text-white">Live Squad Payments</h2>
              <Link to="/payments" className="text-xs font-semibold text-pink-400 hover:text-pink-300">
                All TXNs
              </Link>
            </div>

            <div className="space-y-3">
              {recentPayments.length === 0 ? (
                <div className="p-6 text-center text-xs text-zinc-500">
                  No payment transactions recorded yet. Real transactions will appear here as users subscribe.
                </div>
              ) : (
                recentPayments.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#141428] border border-[#1e1e3a]">
                    <div>
                      <p className="text-sm font-semibold text-white">{p.user}</p>
                      <p className="text-xs text-zinc-400">{p.plan} · {p.method}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-extrabold text-emerald-400">{p.amount}</p>
                      <p className="text-[11px] text-zinc-500">{p.time}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-[#1e1e3a] text-center">
            <p className="text-xs text-zinc-400">
              🔒 SquadCo Webhook: Verified via SHA512 signature
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
